import React, { useState } from 'react';
import Dialog from './Dialog';
import useStore from '@store';
import { Unlock, Lock, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';

export default function PasswordDialog() {
  const { showPasswordDialog, closePasswordDialog, unlockList } = useStore();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    setIsLoading(true);
    
    const success = await unlockList(password);
    
    if (success) {
      toast.success('List unlocked! You can now edit.');
      closePasswordDialog();
      setPassword(''); // Clear password on success
    } else {
      setError('Invalid password. Please try again.');
    }
    
    setIsLoading(false);
  };
  
  return (
    <Dialog
      isOpen={showPasswordDialog}
      onClose={() => {
        closePasswordDialog();
        setPassword('');
        setError('');
      }}
      title="This List is Protected"
      description="Enter the password to edit this list"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* Lock Icon Visual */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '8px'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#fef3c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Lock style={{ width: '32px', height: '32px', color: '#d97706' }} />
          </div>
        </div>

        {/* Password Input */}
        <div>
          <label htmlFor="password" style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '500', 
            marginBottom: '8px',
            color: '#374151'
          }}>
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(''); // Clear error when typing
              }}
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: '40px',
                border: `1px solid ${error ? '#ef4444' : '#d1d5db'}`,
                borderRadius: '8px',
                fontSize: '16px',
                outline: 'none',
                fontFamily: 'monospace',
                backgroundColor: error ? '#fef2f2' : 'white',
                transition: 'border-color 0.2s'
              }}
              placeholder="Enter the list password"
              autoFocus
              autoComplete="off"
              onFocus={(e) => e.target.style.borderColor = error ? '#ef4444' : '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = error ? '#ef4444' : '#d1d5db'}
            />
            
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                padding: '4px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 
                <EyeOff style={{ width: '18px', height: '18px', color: '#6b7280' }} /> :
                <Eye style={{ width: '18px', height: '18px', color: '#6b7280' }} />
              }
            </button>
          </div>
          
          {error && (
            <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px', fontWeight: '500' }}>
              {error}
            </p>
          )}
        </div>

        {/* Info Box */}
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <h4 style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            marginBottom: '8px',
            color: '#0c4a6e'
          }}>
            About password protection:
          </h4>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '0',
            listStyle: 'none',
            fontSize: '13px',
            color: '#0369a1',
            lineHeight: '1.5'
          }}>
            <li style={{ marginBottom: '4px' }}>• You can view this list without the password</li>
            <li style={{ marginBottom: '4px' }}>• The password is required to make edits</li>
            <li>• Ask the list owner for the password if you need to edit</li>
          </ul>
        </div>
        
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
          <button
            type="button"
            onClick={() => {
              closePasswordDialog();
              setPassword('');
              setError('');
            }}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
          >
            View Only
          </button>
          <button
            type="submit"
            disabled={isLoading || !password}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: isLoading || !password ? '#9ca3af' : '#059669',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              cursor: isLoading || !password ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!isLoading && password) {
                e.target.style.backgroundColor = '#047857';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading && password) {
                e.target.style.backgroundColor = '#059669';
              }
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Checking...
              </>
            ) : (
              <>
                <Unlock style={{ width: '16px', height: '16px' }} />
                Unlock & Edit
              </>
            )}
          </button>
        </div>
      </form>
    </Dialog>
  );
}